// Builds the scope dropdown lists. Shared by both data paths.
//
// Everything here needs identity columns only -- no measures -- so the rollup
// path can serve it from the narrow doctor-week rows it already fetches unscoped.
// Kept as one function rather than one per path because these lists ARE the
// navigation: a level that silently offers a different set on one path than the
// other is a bug nobody would think to look for.

import type { ScopeFilters, ScopeOptions } from "@/types/filters";

/** The only columns the dropdowns read. SchedulingRow satisfies this structurally. */
export interface ScopeIdentityRow {
  country_code: string;
  group_name: string;
  division_name: string;
  location_id: string;
  location_name: string;
  resource_id: string;
  resource_full_name: string;
}

// Two different doctors really can share a name -- 99441 and 36101 are both
// "Melissa Colon" -- and sorting on name alone leaves their order decided by
// whichever order the rows happened to arrive in. That is not stable even on the
// raw path, whose query orders by date and says nothing about ties within a day;
// compare mode caught it as the two paths disagreeing. The id breaks the tie so
// the list is the same list every time, on both paths.
function byNameThenId(a: { id: string; name: string }, b: { id: string; name: string }): number {
  return a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
}

function unique<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}

// Rows matching every scope field EXCEPT those in `exclude`, so each dropdown
// shows options consistent with the other active filters.
function filterExcluding(
  rows: ScopeIdentityRow[],
  scope: ScopeFilters,
  exclude: (keyof ScopeFilters)[]
): ScopeIdentityRow[] {
  const ex = new Set(exclude);
  return rows.filter(
    (r) =>
      (ex.has("countryCode") || !scope.countryCode || r.country_code === scope.countryCode) &&
      (ex.has("groupName") || !scope.groupName || r.group_name === scope.groupName) &&
      (ex.has("divisionName") || !scope.divisionName || r.division_name === scope.divisionName) &&
      (ex.has("locationId") || !scope.locationId || r.location_id === scope.locationId) &&
      (ex.has("resourceId") || !scope.resourceId || r.resource_id === scope.resourceId)
  );
}

// Picking a group, division, site or doctor fills in everything ABOVE it, so
// switching to a site in another division is one click rather than four. That
// needs a row to read the hierarchy off, which is why the raw path kept every row
// in the browser for it.
//
// Shared by both paths, and taking plain identity rows, so the rollup path can
// answer it from the unscoped doctor-week rows it already has. Returns null when
// the patch is not a reverse-fill case, leaving the caller to apply it normally.
export function resolveReverseFill(
  rows: ScopeIdentityRow[],
  patch: Partial<ScopeFilters>
): ScopeFilters | null {
  if ("groupName" in patch && patch.groupName) {
    const row = rows.find((r) => r.group_name === patch.groupName);
    if (!row) return null;
    return {
      countryCode: row.country_code,
      groupName: patch.groupName,
      divisionName: null,
      locationId: null,
      resourceId: null,
    };
  }
  if ("divisionName" in patch && patch.divisionName) {
    const row = rows.find((r) => r.division_name === patch.divisionName);
    if (!row) return null;
    return {
      countryCode: row.country_code,
      groupName: row.group_name,
      divisionName: patch.divisionName,
      locationId: null,
      resourceId: null,
    };
  }
  if ("locationId" in patch && patch.locationId) {
    const row = rows.find((r) => r.location_id === patch.locationId);
    if (!row) return null;
    return {
      countryCode: row.country_code,
      groupName: row.group_name,
      divisionName: row.division_name,
      locationId: patch.locationId,
      resourceId: null,
    };
  }
  if ("resourceId" in patch && patch.resourceId) {
    const dvmRows = rows.filter((r) => r.resource_id === patch.resourceId);
    const one = <T>(values: T[]): T | null => {
      const set = [...new Set(values)];
      return set.length === 1 ? set[0]! : null;
    };
    return {
      countryCode: one(dvmRows.map((r) => r.country_code)),
      groupName: one(dvmRows.map((r) => r.group_name)),
      divisionName: one(dvmRows.map((r) => r.division_name)),
      // Single-site DVM: pin the site. Multi-site DVM: show all their sites.
      locationId: one(dvmRows.map((r) => r.location_id)),
      resourceId: patch.resourceId,
    };
  }
  return null;
}

export interface ScopeCounts {
  siteCount: number;
  dvmCount: number;
  nvaSiteCount: number;
}

// The counts in the header. Same identity rows, same reason to share one
// implementation: nvaSiteCount is deliberately UNSCOPED -- it is the size of the
// benchmark, not of the selection -- and that is exactly the sort of detail a
// second copy quietly gets wrong.
export function buildScopeCounts(rows: ScopeIdentityRow[], scope: ScopeFilters): ScopeCounts {
  const inScope = filterExcluding(rows, scope, []);
  return {
    siteCount: new Set(inScope.map((r) => r.location_id)).size,
    dvmCount: new Set(inScope.map((r) => r.resource_id)).size,
    nvaSiteCount: new Set(rows.map((r) => r.location_id)).size,
  };
}

export function buildScopeOptions(rows: ScopeIdentityRow[], scope: ScopeFilters): ScopeOptions {
  const countries = unique(
    filterExcluding(rows, scope, ["countryCode"]).map((r) => r.country_code)
  ).sort();

  const groups = unique(
    filterExcluding(rows, scope, ["groupName"]).map((r) => r.group_name)
  ).sort();

  const divisions = unique(
    filterExcluding(rows, scope, ["divisionName"]).map((r) => r.division_name)
  ).sort();

  const siteMap = new Map<string, string>();
  filterExcluding(rows, scope, ["locationId"]).forEach((r) =>
    siteMap.set(r.location_id, r.location_name)
  );
  const sites = Array.from(siteMap.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => parseInt(a.id) - parseInt(b.id));

  const dvmMap = new Map<string, string>();
  filterExcluding(rows, scope, ["resourceId"]).forEach((r) =>
    dvmMap.set(r.resource_id, r.resource_full_name)
  );
  const dvms = Array.from(dvmMap.entries())
    .map(([id, name]) => ({ id, name }))
    .sort(byNameThenId);

  // Everything outside the current scope -- selecting one reverse-fills the
  // hierarchy, so switching to a site/DVM in another division is one click.
  const allSiteMap = new Map<string, string>();
  rows.forEach((r) => allSiteMap.set(r.location_id, r.location_name));
  const otherSites = Array.from(allSiteMap.entries())
    .filter(([id]) => !siteMap.has(id))
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => parseInt(a.id) - parseInt(b.id));

  const allDvmMap = new Map<string, string>();
  rows.forEach((r) => allDvmMap.set(r.resource_id, r.resource_full_name));
  const otherDvms = Array.from(allDvmMap.entries())
    .filter(([id]) => !dvmMap.has(id))
    .map(([id, name]) => ({ id, name }))
    .sort(byNameThenId);

  return { countries, divisions, groups, sites, dvms, otherSites, otherDvms };
}
